import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  Legend,
  CartesianGrid,
} from 'recharts';
import AdminLayout from '../../layouts/AdminLayout';
import { AdminService } from '../../services/adminService';

/**
 * L'entonnoir d'achat : offres vues → paiement ouvert → payé, abandonné, refusé. Tout est
 * calculé par le serveur (`services/entonnoirAchat.service.js`) ; cet écran ne fait qu'afficher.
 */

const nombre = (n) => Math.round(n || 0).toLocaleString('fr-FR');
const pourcent = (t) => (t == null ? '—' : `${t.toLocaleString('fr-FR')} %`);
const formatSemaine = (iso) =>
  iso ? new Date(`${iso}T12:00:00`).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }) : '';

const LIBELLES_ISSUE = {
  annulee: 'Annulée par l’acheteur',
  echec: 'Refusée (store ou banque)',
  en_attente: 'En attente (accord d’un parent, paiement lent)',
  non_verifie: 'Payée, non vérifiée par le serveur',
};
const LIBELLES_STATUT = {
  completed: 'Payé', payee: 'Payé', pending: 'En cours', en_attente: 'En cours',
  expired: 'Abandonné', abandonnee: 'Abandonné', remboursee: 'Remboursé',
};
const SERIES = [
  { cle: 'offres_vues', libelle: 'Offres vues', couleur: '#8884d8' },
  { cle: 'paiements_ouverts', libelle: 'Paiements ouverts', couleur: '#f0a020' },
  { cle: 'payes', libelle: 'Payés', couleur: '#2e7d32' },
  { cle: 'annulees', libelle: 'Annulés (stores)', couleur: '#9e9e9e' },
  { cle: 'echecs', libelle: 'Refusés', couleur: '#c62828' },
];

function Chiffre({ titre, valeur, detail }) {
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="overline" color="text.secondary">{titre}</Typography>
        <Typography variant="h5">{valeur}</Typography>
        {detail ? <Typography variant="body2" color="text.secondary">{detail}</Typography> : null}
      </CardContent>
    </Card>
  );
}

function Tableau({ titre, sousTitre, colonnes, lignes, vide }) {
  return (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardHeader title={titre} subheader={sousTitre} />
      <CardContent>
        {lignes.length === 0 ? (
          <Typography variant="body2" color="text.secondary">{vide}</Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {colonnes.map((c) => <TableCell key={c.cle} align={c.nombre ? 'right' : 'left'}>{c.titre}</TableCell>)}
                </TableRow>
              </TableHead>
              <TableBody>
                {lignes.map((l, i) => (
                  <TableRow key={i}>
                    {colonnes.map((c) => (
                      <TableCell key={c.cle} align={c.nombre ? 'right' : 'left'}>
                        {c.rendu ? c.rendu(l) : (c.nombre ? nombre(l[c.cle]) : (l[c.cle] || '—'))}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
}

/** Les lignes `{ semaine, quoi, n }` du serveur, une ligne par semaine pour le graphique. */
function parSemaine(lignes) {
  const semaines = new Map();
  for (const { semaine, quoi, n } of lignes || []) {
    if (!semaines.has(semaine)) semaines.set(semaine, { semaine });
    semaines.get(semaine)[quoi] = n;
  }
  return [...semaines.values()].sort((a, b) => a.semaine.localeCompare(b.semaine));
}

export default function EntonnoirAchatScreen() {
  const [jours, setJours] = useState(30);
  const [bilan, setBilan] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);

  const charger = useCallback(() => {
    setChargement(true);
    return AdminService.getEntonnoirAchat(jours)
      .then((b) => { setBilan(b); setErreur(null); })
      .catch((err) => setErreur(err.response?.data?.error || 'Impossible de charger l’entonnoir d’achat.'))
      .finally(() => setChargement(false));
  }, [jours]);

  useEffect(() => { charger(); }, [charger]);

  const r = bilan?.resume;
  const serie = parSemaine(bilan?.semaines);
  const paiementsStripe = [
    ...(bilan?.stripeAbonnements || []).map((l) => ({ produit: 'Abonnement', ...l })),
    ...(bilan?.achatsUniques || []).map((l) => ({ ...l })),
  ];

  return (
    <AdminLayout>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4">Entonnoir d’achat</Typography>
        <ToggleButtonGroup size="small" exclusive value={jours} onChange={(_, v) => v && setJours(v)}>
          {[7, 30, 90, 365].map((j) => <ToggleButton key={j} value={j}>{j} jours</ToggleButton>)}
        </ToggleButtonGroup>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Les offres vues dans l’app ne comptent que les joueurs qui ont accepté la mesure d’audience : c’est un
        minorant, à lire en tendance. Au portail parent, elles sont comptées sans identifiant. Un paiement Stripe
        ouvert puis laissé passe « abandonné » quand sa session expire (24 h). Sur les stores, l’app rapporte
        l’issue de la feuille d’achat (depuis la version qui porte cette mesure).
      </Typography>

      {erreur ? <Alert severity="error" sx={{ mb: 3 }}>{erreur}</Alert> : null}
      {chargement && !bilan ? <CircularProgress /> : null}

      {r ? (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} lg={3}>
              <Chiffre titre="Offres vues" valeur={nombre(r.offresVues)}
                detail={`${nombre(r.offresTouchees)} touchées (${pourcent(r.tauxClic)})`} />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <Chiffre titre="Paiements Stripe" valeur={`${nombre(r.stripe.payes)} / ${nombre(r.stripe.ouverts)}`}
                detail={`payés / ouverts (${pourcent(r.stripe.tauxConversion)}) · ${nombre(r.stripe.abandonnes)} abandonnés · ${nombre(r.stripe.enCours)} en cours`} />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <Chiffre titre="Achats sur les stores" valeur={`${nombre(r.stores.payes)} / ${nombre(r.stores.tentes)}`}
                detail={`payés / tentés (${pourcent(r.stores.tauxConversion)}) · ${nombre(r.stores.annules)} annulés · ${nombre(r.stores.enAttente)} en attente`} />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <Chiffre titre="Échecs" valeur={nombre(r.stores.echecs + r.stripe.refus)}
                detail={`${nombre(r.stores.echecs)} sur les stores · ${nombre(r.stripe.refus)} prélèvements Stripe refusés`} />
            </Grid>
          </Grid>

          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardHeader title="Par semaine" subheader="Semaines commençant le lundi" />
            <CardContent sx={{ height: 320 }}>
              {serie.length === 0 ? (
                <Typography variant="body2" color="text.secondary">Rien sur la période.</Typography>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={serie}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="semaine" tickFormatter={formatSemaine} />
                    <YAxis allowDecimals={false} />
                    <RechartsTooltip labelFormatter={(s) => `Semaine du ${formatSemaine(s)}`} />
                    <Legend />
                    {SERIES.map((s) => <Bar key={s.cle} dataKey={s.cle} name={s.libelle} fill={s.couleur} />)}
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          <Tableau
            titre="Offres vues et touchées"
            sousTitre="Par surface, lieu et fonctionnalité verrouillée"
            vide="Aucune offre mesurée sur la période."
            colonnes={[
              { cle: 'surface', titre: 'Surface' },
              { cle: 'lieu', titre: 'Lieu' },
              { cle: 'fonctionnalite', titre: 'Fonctionnalité' },
              { cle: 'plateforme', titre: 'Plateforme' },
              { cle: 'geste', titre: 'Geste', rendu: (l) => (l.geste === 'clic' ? 'Touchée' : 'Vue') },
              { cle: 'n', titre: 'Nombre', nombre: true },
              { cle: 'sessions', titre: 'Sessions (app)', nombre: true },
            ]}
            lignes={bilan.offres}
          />

          <Tableau
            titre="Paiements Stripe"
            sousTitre="Webapp, portail parent, site : abonnements, pass, histoires, Grimoire, cahier"
            vide="Aucun paiement ouvert sur la période."
            colonnes={[
              { cle: 'produit', titre: 'Produit' },
              { cle: 'statut', titre: 'Issue', rendu: (l) => LIBELLES_STATUT[l.statut] || l.statut },
              { cle: 'n', titre: 'Nombre', nombre: true },
            ]}
            lignes={paiementsStripe}
          />

          <Tableau
            titre="Achats réussis sur les stores"
            sousTitre="Abonnements, pass et bons pour une histoire (App Store, Google Play)"
            vide="Aucun achat sur les stores sur la période."
            colonnes={[
              { cle: 'plateforme', titre: 'Plateforme' },
              { cle: 'produit', titre: 'Produit', rendu: (l) => (l.produit === 'bon_histoire' ? 'Bon pour une histoire' : l.produit === 'abonnement' ? 'Abonnement' : l.produit) },
              { cle: 'n', titre: 'Nombre', nombre: true },
            ]}
            lignes={bilan.storesReussis}
          />

          <Tableau
            titre="Tentatives qui n’aboutissent pas"
            sousTitre="Stores (rapportées par l’app) et prélèvements Stripe refusés"
            vide="Aucune tentative manquée sur la période."
            colonnes={[
              { cle: 'plateforme', titre: 'Plateforme' },
              { cle: 'nature', titre: 'Nature' },
              { cle: 'issue', titre: 'Issue', rendu: (l) => LIBELLES_ISSUE[l.issue] || l.issue },
              { cle: 'code', titre: 'Code' },
              { cle: 'lieu', titre: 'Lieu' },
              { cle: 'n', titre: 'Nombre', nombre: true },
              { cle: 'comptes', titre: 'Comptes', nombre: true },
            ]}
            lignes={bilan.tentatives}
          />
        </>
      ) : null}
    </AdminLayout>
  );
}
